import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Divider, Form, Row } from 'antd';
import { omit } from 'es-toolkit';

import { formatDate } from '@/utils/date';
import { convertFromESTToLocal } from '@/utils/date/temporary';
import { exportOverChargeClaimsToExcel } from '@/utils/excel';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { DEFAULT_DATE_FORMAT, OVERCHARGE_CLAIMS_EXPORT_FIELDS } from '../../../../../contants/common';
import { setAppLoader } from '../../../../../redux/common/CommonActions';
import { routeFileSelector } from '../../../../../utilz/i18n';
import FormCheckBox from '../../../../components/Inputs/FormCheckBox';
import CommonModal from '../../../../components/Modals/CommonModal';
import { getOverChargeClaimsExportAPI } from '../data-provider/claims-export/api';

const OverChargeExportClaimsModal = ({ open = true, setOpen, isSelectAll = false }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { claimsFilters, selectedClaims } = useSelector((state) => state.overChargeClaims);

  const formData = Form.useWatch(null, form);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const selectedAll = useMemo(
    () =>
      formData
        ? OVERCHARGE_CLAIMS_EXPORT_FIELDS.map(({ value }) => formData[value])?.filter((value) => value).length ===
          OVERCHARGE_CLAIMS_EXPORT_FIELDS.length
        : false,
    [formData],
  );

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSelectAllChange = (e) => {
    const fieldObjects = {};
    const newValue = Boolean(e?.target?.checked);
    OVERCHARGE_CLAIMS_EXPORT_FIELDS.forEach(({ value }) => {
      fieldObjects[value] = newValue;
    });
    form.setFieldsValue(fieldObjects);
  };

  const requestParams = useMemo(
    () => ({
      ...omit(claimsFilters, ['byAge', 'pageSize', 'pageNo']),
      claimNo: isSelectAll ? null : selectedClaims?.length > 0 ? selectedClaims.join(',') : undefined,
      EndDate: claimsFilters.EndDate,
      fbocKey: isSelectAll ? null : selectedClaims?.length > 0 ? selectedClaims.join(',') : undefined,
      StartDate: claimsFilters.StartDate,
      StatusCodes: claimsFilters.StatusCodes?.join(',') || '',
    }),
    [claimsFilters, isSelectAll, selectedClaims],
  );

  const handleExportButtonClick = async () => {
    try {
      dispatch(setAppLoader(true));
      const formData = await form.getFieldsValue();
      const selectedFields = [];
      OVERCHARGE_CLAIMS_EXPORT_FIELDS.forEach(({ label, value, columnName }) => {
        if (formData[value] === true) {
          selectedFields.push({ columnName, label, value });
        }
      });
      if (selectedFields.length > 0) {
        fetchDataForExport(selectedFields);
      } else {
        showNotification({
          message: t('translation:claims.selectMinOneField'),
          type: NOTIFICATION_TYPES.ERROR,
        });
        dispatch(setAppLoader(false));
      }
    } catch {
      dispatch(setAppLoader(false));
    }
  };

  const fetchDataForExport = async (selectedColumns) => {
    try {
      const { data } = await getOverChargeClaimsExportAPI(requestParams);
      const claimsData =
        data?.claimsData?.map((row) => ({
          ...row,
          ammendmentDate: formatDate(convertFromESTToLocal(row.ammendmentDate), DEFAULT_DATE_FORMAT),
          closedDate: formatDate(convertFromESTToLocal(row.closedDate), DEFAULT_DATE_FORMAT),
          dateSubmitted: formatDate(convertFromESTToLocal(row.dateSubmitted), DEFAULT_DATE_FORMAT),
        })) || [];
      await exportOverChargeClaimsToExcel(selectedColumns, claimsData);
      setOpen(false);
      dispatch(setAppLoader(false));
    } catch (error) {
      console.log(error);
      showNotification({
        message: 'Failed to export claims.',
        type: NOTIFICATION_TYPES.ERROR,
      });
      dispatch(setAppLoader(false));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setOpen(false);
  };

  return (
    <CommonModal
      open={open}
      onClose={() => setOpen(false)}
      title={t('translation:claims.chooseColumns')}
      okText={t('translation:default.buttons.export')}
      cancelText={t('translation:default.buttons.cancel')}
      className="w-70"
      contentClassName="p-0"
      descriptionClassName="d-flex flex-direction-column"
      footerClassName="px-30 pb-15"
      onOk={handleExportButtonClick}
      onCancel={handleCancel}
    >
      <div className="flex-1 overflow-hidden">
        <Row className="h-100 overflow-hidden flex-direction-column flex-now-wrap">
          <Col className="py-15 px-30">
            <FormCheckBox
              className="m-0"
              checkBoxClass="normal-checkbox"
              label={t('translation:default.fields.selectAll')}
              checked={selectedAll}
              onChange={handleSelectAllChange}
            />
          </Col>
          <Col>
            <Divider className="m-0" />
          </Col>
          <Col className="flex-1 p-30 custom-scroll-bar">
            <Form form={form}>
              <Row gutter={15}>
                {OVERCHARGE_CLAIMS_EXPORT_FIELDS.map((row) => (
                  <Col key={row.value} lg={6}>
                    <FormCheckBox checkBoxClass="normal-checkbox" label={row.label} name={row.value} />
                  </Col>
                ))}
              </Row>
            </Form>
          </Col>
        </Row>
      </div>
    </CommonModal>
  );
};

export default OverChargeExportClaimsModal;
